import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CreateServicePrincipalDto,
  GetServicePrincipalsParams,
  IServicePrincipal,
  IServicePrincipalDetail,
  IServicePrincipalRole,
  ServicePrincipalListResponse,
  ServicePrincipalStatus,
  UpdateServicePrincipalDto,
} from '~/Services/ServicePrincipal.interface'
import { PageMeta } from '~/Types/Common'
import { ModalName } from './constants'

interface ModalVisibility {
  name: string
  visible: boolean
}

interface ServicePrincipalState {
  isLoading: boolean
  isConfirmLoading: boolean
  isDetailLoading: boolean
  isRoleActionLoading: boolean
  servicePrincipals: IServicePrincipal[]
  servicePrincipalSelected?: IServicePrincipal
  servicePrincipalDetail?: IServicePrincipalDetail
  pageMeta?: PageMeta
  // Status of the currently active list tab (Active/Inactive/Suspended) — kept
  // here (not just as component state) so epics that refetch the list after a
  // create/update/delete (which don't know which tab the view is on) can
  // re-apply the same filter instead of dropping it and pulling every status.
  currentStatus?: ServicePrincipalStatus
  modalVisibility: {
    [id: string]: { visible: boolean }
  }
  // Roles shown per-row in the list table (~ Organization Users tab's Roles
  // column) — keyed by servicePrincipalId, separate from the single
  // `servicePrincipalDetail` used by the detail modal.
  rolesByServicePrincipal: { [servicePrincipalId: string]: IServicePrincipalRole[] }
  loadingRolesByServicePrincipal: { [servicePrincipalId: string]: boolean }
}

const initialState: ServicePrincipalState = {
  isLoading: false,
  isConfirmLoading: false,
  isDetailLoading: false,
  isRoleActionLoading: false,
  servicePrincipals: [],
  modalVisibility: {},
  rolesByServicePrincipal: {},
  loadingRolesByServicePrincipal: {},
}

export const ServicePrincipalSlice = createSlice({
  name: 'servicePrincipal',
  initialState,
  reducers: {
    getServicePrincipals: (
      state,
      action: PayloadAction<GetServicePrincipalsParams | undefined>,
    ) => {
      state.isLoading = true
      state.currentStatus = action.payload?.status
    },
    getServicePrincipalsSuccess: (
      state,
      action: PayloadAction<ServicePrincipalListResponse>,
    ) => {
      const {
        results,
        page,
        pageCount,
        pageSize,
        queryCount,
        firstRowIndex,
        lastRowIndex,
      } = action.payload
      state.servicePrincipals = results
      state.pageMeta = {
        page,
        pageCount,
        pageSize,
        queryCount,
        firstRowIndex,
        lastRowIndex,
      }
      state.isLoading = false
    },
    getServicePrincipalsFail: state => {
      state.isLoading = false
    },
    setModalVisibility: (state, action: PayloadAction<ModalVisibility>) => {
      const { name, visible } = action.payload
      state.modalVisibility[name] = { visible }
    },
    setServicePrincipalSelected: (
      state,
      action: PayloadAction<IServicePrincipal | undefined>,
    ) => {
      state.servicePrincipalSelected = action.payload
    },
    // `organizationId` is filled in by the epic from the current Organization,
    // so views only supply the fields a user actually types.
    createServicePrincipal: (
      state,
      action: PayloadAction<Omit<CreateServicePrincipalDto, 'organizationId'>>,
    ) => {
      state.isConfirmLoading = true
    },
    createServicePrincipalSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.ServicePrincipal] = { visible: false }
    },
    createServicePrincipalFail: state => {
      state.isConfirmLoading = false
    },
    updateServicePrincipal: (
      state,
      action: PayloadAction<UpdateServicePrincipalDto & { id: string }>,
    ) => {
      state.isConfirmLoading = true
    },
    updateServicePrincipalSuccess: (
      state,
      action: PayloadAction<IServicePrincipal>,
    ) => {
      state.isConfirmLoading = false
      const index = state.servicePrincipals.findIndex(
        x => x.id === action.payload.id,
      )
      if (index !== -1) {
        // A status-change action (Activate/Inactivate/Suspend) may move the
        // item out of the tab currently being viewed — drop it from the list
        // instead of leaving it displayed under the wrong status until the
        // next full refetch.
        if (
          state.currentStatus !== undefined &&
          action.payload.status !== state.currentStatus
        ) {
          state.servicePrincipals.splice(index, 1)
        } else {
          state.servicePrincipals[index] = action.payload
        }
      }
      state.modalVisibility[ModalName.ServicePrincipal] = { visible: false }
    },
    updateServicePrincipalFail: state => {
      state.isConfirmLoading = false
    },
    deleteServicePrincipal: (state, action: PayloadAction<string>) => {
      state.isConfirmLoading = true
    },
    deleteServicePrincipalSuccess: state => {
      state.isConfirmLoading = false
    },
    deleteServicePrincipalFail: state => {
      state.isConfirmLoading = false
    },
    getServicePrincipalDetail: (state, action: PayloadAction<string>) => {
      state.isDetailLoading = true
    },
    getServicePrincipalDetailSuccess: (
      state,
      action: PayloadAction<IServicePrincipalDetail>,
    ) => {
      state.servicePrincipalDetail = action.payload
      state.isDetailLoading = false
    },
    getServicePrincipalDetailFail: state => {
      state.isDetailLoading = false
    },
    assignRole: (
      state,
      action: PayloadAction<{ servicePrincipalId: string; roleId: string }>,
    ) => {
      state.isRoleActionLoading = true
    },
    assignRoleSuccess: (state, action: PayloadAction<IServicePrincipalRole>) => {
      state.isRoleActionLoading = false
      if (state.servicePrincipalDetail) {
        state.servicePrincipalDetail.roles.push(action.payload)
        // Keep the list table's Roles column (a separate per-row cache) in
        // sync so it doesn't go stale until the whole page reloads.
        const rowRoles = state.rolesByServicePrincipal[state.servicePrincipalDetail.id]
        if (rowRoles) {
          rowRoles.push(action.payload)
        }
      }
    },
    assignRoleFail: state => {
      state.isRoleActionLoading = false
    },
    removeRole: (
      state,
      action: PayloadAction<{ servicePrincipalId: string; roleId: string }>,
    ) => {
      state.isRoleActionLoading = true
    },
    removeRoleSuccess: (state, action: PayloadAction<string>) => {
      state.isRoleActionLoading = false
      if (state.servicePrincipalDetail) {
        state.servicePrincipalDetail.roles =
          state.servicePrincipalDetail.roles.filter(
            r => r.roleId !== action.payload,
          )
        // Keep the list table's Roles column (a separate per-row cache) in
        // sync so it doesn't go stale until the whole page reloads.
        const spId = state.servicePrincipalDetail.id
        if (state.rolesByServicePrincipal[spId]) {
          state.rolesByServicePrincipal[spId] = state.rolesByServicePrincipal[
            spId
          ].filter(r => r.roleId !== action.payload)
        }
      }
    },
    removeRoleFail: state => {
      state.isRoleActionLoading = false
    },
    getServicePrincipalRoles: (state, action: PayloadAction<string>) => {
      state.loadingRolesByServicePrincipal[action.payload] = true
    },
    getServicePrincipalRolesSuccess: (
      state,
      action: PayloadAction<{
        servicePrincipalId: string
        roles: IServicePrincipalRole[]
      }>,
    ) => {
      const { servicePrincipalId, roles } = action.payload
      state.rolesByServicePrincipal[servicePrincipalId] = roles
      state.loadingRolesByServicePrincipal[servicePrincipalId] = false
    },
    getServicePrincipalRolesFail: (state, action: PayloadAction<string>) => {
      state.loadingRolesByServicePrincipal[action.payload] = false
    },
  },
})

export const ServicePrincipalActions = ServicePrincipalSlice.actions
export const ServicePrincipalReducer = ServicePrincipalSlice.reducer
